import {useMediaQuery} from '@mui/material'
import {FC, useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useNavigate, Link} from 'react-router-dom'
import {clearNewNotificationCount, useNewNotificationCount, useNotificationsList} from 'entities/activity/model'
import {clearUser, useUser} from 'entities/user'
import {ChatRequestsList} from 'features/chatRequestsList'
import {FreshChatOpenSupport} from 'features/freshChat'
import {NotificationItem} from 'features/notificationItem'
import {upgradeHandler} from 'features/payment/lib'
import {mediaVariants} from 'shared/lib'
import {displayCount} from 'shared/lib/entity'
import {text} from 'shared/mirrors/text'
import {UserStatus} from 'shared/types'
import {Avatar} from 'shared/ui/avatar'
import {Badge} from 'shared/ui/badge'
import {Button} from 'shared/ui/buttons'
import {HeadphoneOutline, Logout, SettingsOutline} from 'shared/ui/icons'
import {Switcher} from 'shared/ui/switcher'
import {ArrowDown, Logo, Notification} from './img/icons'
import styles from './styles.module.scss'

export const HeaderDesktop: FC = () => {
  const dispatch = useDispatch()
  const {profile, paymentsCount, status} = useUser()
  const [isShowPopup, setIsShowPopup] = useState(false)
  const [notificationsIsShowPopup, setNotificationsIsShowPopup] = useState<boolean>(false)
  const isBackdrop = isShowPopup || notificationsIsShowPopup
  const newNotificationCount = useNewNotificationCount()

  const notificationClickHandler = () => {
    setNotificationsIsShowPopup(!notificationsIsShowPopup)
    setIsShowPopup(false)
  }

  const avatarClickHandler = () => {
    setIsShowPopup(!isShowPopup)
    setNotificationsIsShowPopup(false)
  }

  const backdropClickHandler = () => {
    setNotificationsIsShowPopup(false)
    setIsShowPopup(false)
  }

  const isActive = useMemo(() => status === UserStatus.ACTIVE, [status])

  return (
    <div className={styles.header}>
      {isBackdrop ? <div className={styles.backdrop} onClick={backdropClickHandler} /> : null}
      <div className={styles.headerWrapper}>
        <Link to='/' className={styles.wrapperLogo}>
          <div className={styles.wrapperLogoIcon}><Logo /></div>
          <div className={styles.text}>{text.main.siteName}</div>
        </Link>
        <div className={styles.wrapperInfo}>
          {/* TODO: uncomment when it needed */}
          {/* <div className={styles.specialOffer} onClick={handleClickOffer}>
            <div className={styles.icon}><SpecialOffer /><
            Special offer
          </div> */}
          {isActive ? <div className={styles.updateNow} onClick={() => upgradeHandler({dispatch, paymentsCount})}>
            {paymentsCount > 0 ? 'Refill credits' : 'UPGRADE NOW'}
          </div> : null}
          <div className={styles.wrapperControls}>
            {isActive ? <div
              className={`${styles.notification} ${notificationsIsShowPopup ? styles.active : ''}`}
              onClick={notificationClickHandler}
            >
              <div className={styles.icon}><Notification /></div>
              <NotificationsPopup
                isOpen={notificationsIsShowPopup}
                onClose={() => setNotificationsIsShowPopup(false)}
              />
              {newNotificationCount
                ? <Badge className={styles.count} type='red'>{displayCount(newNotificationCount)}</Badge>
                : null}
            </div> : null}

            <div className={`${styles.avatarWrapper} ${isShowPopup ? styles.active : ''}`} onClick={avatarClickHandler}>
              <div className={styles.avatar}>
                <Avatar
                  sx={{width: 36, height: 36}}
                  src={profile.avatarUrl ? profile.avatarUrl + mediaVariants.square64 : ''}
                  alt={profile.name}
                />
                <ArrowDown />
              </div>
              {isShowPopup ? <UserPopup
                isActive={isActive}
                name={profile.name}
                email={profile.email}
                avatarUrl={profile.avatarUrl ? profile.avatarUrl + mediaVariants.square64 : ''}
                onClose={() => setIsShowPopup(false)}
              /> : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

type UserPopupProps = {
  avatarUrl: string,
  name: string,
  email: string,
  isActive: boolean
  onClose: () => void
}

const UserPopup: FC<UserPopupProps> = ({avatarUrl, name, email, onClose, isActive}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {paymentsCount, support: {enabled}} = useUser()

  const signOut = () => {
    dispatch(clearUser())
    location.reload()
  }

  return (
    <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
      <div className={styles.topContainer}>
        <div className={styles.user}>
          <Avatar src={avatarUrl} />
          <div className={styles.text}>
            <span className={styles.name}>{name}</span>
            <span className={styles.email}>{email}</span>
          </div>
        </div>
        {isActive ? <Button
          className={styles.upgradeBtn}
          styleType='primary'
          onClick={()=> upgradeHandler({dispatch, paymentsCount})}
        >
          {paymentsCount > 0 ? 'Refill credits' : 'Upgrade account'}
        </Button> : null}
      </div>
      <div className={styles.menuContainer}>
        {/*<div className={styles.menuItem}>
          <div className={styles.menuItem__icon}> <DarkMode /> </div>
          <span>Dark mode</span>
        </div>*/}
        {isActive ? <div
          className={styles.menuItem}
          onClick={() => {
            onClose()
            navigate('/settings')
          }}
        >
          <div className={styles.menuItem__icon}><SettingsOutline /> </div>
          <span>Settings</span>
        </div> : null}
        {enabled ? <div className={styles.menuItem} onClick={FreshChatOpenSupport}>
          <div className={styles.menuItem__icon}><HeadphoneOutline /></div>
          <span>Support</span>
        </div> : null}
        <div className={styles.menuItem} onClick={signOut}>
          <div className={`${styles.menuItem__icon} ${styles.menuItem__logout}`}> <Logout /> </div>
          <span className={styles.signOut}>Sign out</span>
        </div>
      </div>
      <div className={styles.footer}>
        <Link to='/privacy-policy' className={styles.text}>Privacy</Link>
        <span className={styles.text}>·</span>
        <Link to='/terms-of-use' className={styles.text}>Terms</Link>
        <span className={styles.text}>·</span>
        <Link to='/dating-safety' className={styles.text}>Dating safety</Link>
        <span className={styles.text}>·</span>
        <Link to='/community-rules' className={styles.text}>Rules</Link>
        <span className={styles.text}>·</span>
        <span className={styles.text}>{text.main.siteName} © {new Date().getFullYear()}</span>
      </div>
    </div>
  )
}

type NotificationsPopupProps = {
  isOpen: boolean
  onClose: () => void
}

const NotificationsPopup: FC<NotificationsPopupProps> = ({isOpen, onClose}) => {
  const dispatch = useDispatch()
  const [switcherValue, setSwitcherValue] = useState(false)
  const is1280p = useMediaQuery(`(max-width: ${styles.is1280p})`)

  const onToggleSwitcher = () => setSwitcherValue(!switcherValue)

  useEffect(() => {
    if (isOpen) dispatch(clearNewNotificationCount())
  }, [isOpen])

  if (!isOpen) return <></>

  if (is1280p) {
    return (
      <div className={styles.notificationPopover} onClick={(e) => e.stopPropagation()}>
        <Switcher values={['Chat requests', 'Notifications']} value={switcherValue} handler={onToggleSwitcher} />

        {!switcherValue ? <ChatRequestsList onClose={onClose} /> : null}
        {switcherValue ? <NotificationsList onClose={onClose} /> : null}
      </div>
    )
  }

  return (
    <div className={styles.notificationPopover} onClick={(e) => e.stopPropagation()}>
      <NotificationsList onClose={onClose} />
    </div>
  )
}

const NotificationsList: FC<{onClose: () => void}> = ({onClose}) => {
  const navigate = useNavigate()
  const list = useNotificationsList()

  const showAllHandler = () => {
    navigate('/notifications')
    onClose()
  }

  if (list.length) {
    return (
      <>
        <div className={`${styles.notificationsList} ${styles.customScroll}`}>
          {list.slice(0, 5).map((notification, index) => <NotificationItem
            key={index}
            className={styles.notificationItem}
            notification={notification}
            isAction={false}
          />)}
        </div>
        <div onClick={showAllHandler} className={styles.notificationsShowAll}>Show All</div>
      </>
    )
  }

  return <div className={styles.notificationsEmpty}>You don't have any notifications yet</div>
}
